import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Divider,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material'
import { ArrowBack, FiberManualRecord, Share, ContentCopy, Delete, Link as LinkIcon } from '@mui/icons-material'
import { statusColor, statusLabel } from '../../lib/deviceStatus'
import { PageHeader } from '../../components/PageHeader'
import { useDevice, useDeviceSensors } from '../../hooks/useDevices'
import { useTwinDevice } from '../../hooks/useDigitalTwin'
import { useFuel, FuelRecord, useTrips, Trip } from '../../hooks/useFleet'
import { useShareLinks, useCreateShareLink, useDeleteShareLink } from '../../hooks/useShareLinks'
import { format } from 'date-fns'
import { toast } from '../../store/toastStore'

export default function DeviceDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: device, isLoading: deviceLoading } = useDevice(id!)
  const { data: sensors = [] } = useDeviceSensors(id!)
  const { data: twin } = useTwinDevice(id!)
  const { data: fuelRecords = [] } = useFuel(id!)
  const { data: trips = [] } = useTrips(id!)
  const { data: shareLinks = [] } = useShareLinks()
  const createShare = useCreateShareLink()
  const deleteShare = useDeleteShareLink()

  const [shareOpen, setShareOpen] = useState(false)
  const [shareLabel, setShareLabel] = useState('')
  const [shareHours, setShareHours] = useState(24)
  const [destLat, setDestLat] = useState('')
  const [destLng, setDestLng] = useState('')
  const [destLabel, setDestLabel] = useState('')

  const deviceShareLinks = shareLinks.filter((s: any) => s.device_id === id)
  const liveStatus = twin?.status ?? device?.status ?? 'offline'

  const handleCreateShare = async () => {
    try {
      const data: any = { deviceId: id!, label: shareLabel || device?.name || '', expiresHours: shareHours }
      const lat = parseFloat(destLat)
      const lng = parseFloat(destLng)
      if (!isNaN(lat) && !isNaN(lng)) {
        data.dest_latitude = lat
        data.dest_longitude = lng
        data.dest_label = destLabel
      }
      await createShare.mutateAsync(data)
      analytics.track(EVENTS.SHARE_LINK_CREATED, { device_id: id, expires_hours: shareHours })
      setShareOpen(false)
      setShareLabel('')
      setShareHours(24)
      setDestLat('')
      setDestLng('')
      setDestLabel('')
      toast.success(t('share.created'))
    } catch {
      // Error toast shown by API interceptor
    }
  }

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    toast.info(t('share.copied'))
  }

  if (deviceLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!device) {
    return <Alert severity="error">{t('device.notFound')}</Alert>
  }

  return (
    <Box>
      <PageHeader
        title={device.name}
        subtitle={`${t('device.identifier')}: ${device.device_identifier}`}
        breadcrumbs={[
          { label: t('device.title'), href: '/devices' },
          { label: device.name },
        ]}
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Share />} onClick={() => setShareOpen(true)}
              sx={{ borderColor: '#34A8E0', color: '#34A8E0' }}>
              {t('share.shareDevice')}
            </Button>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/devices')}>
              {t('common.back')}
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        {/* Device Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('device.info')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('common.status')}</Typography>
                  <Chip
                    icon={<FiberManualRecord sx={{ fontSize: '12px !important', color: `${statusColor(liveStatus)} !important` }} />}
                    label={t(`map.${liveStatus}`, statusLabel(liveStatus))}
                    size="small"
                    sx={{
                      bgcolor: statusColor(liveStatus) + '18',
                      color: statusColor(liveStatus),
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('device.created')}</Typography>
                  <Typography variant="body2">
                    {device.created_at ? format(new Date(device.created_at), 'PP') : '—'}
                  </Typography>
                </Box>
                {device.device_type && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.deviceType')}</Typography>
                    <Chip label={device.device_type.replace(/_/g, ' ')} size="small" variant="outlined" />
                  </Box>
                )}
                {device.vehicle_type && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.vehicleType')}</Typography>
                    <Chip label={device.vehicle_type.replace(/_/g, ' ')} size="small" variant="outlined" />
                  </Box>
                )}
                {device.plate_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.plateNumber')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{device.plate_number}</Typography>
                  </Box>
                )}
                {device.owner_name && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.ownerName')}</Typography>
                    <Typography variant="body2">{device.owner_name}</Typography>
                  </Box>
                )}
                {device.phone_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.phoneNumber')}</Typography>
                    <Typography variant="body2">{device.phone_number}</Typography>
                  </Box>
                )}
                {device.timezone && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.timezone')}</Typography>
                    <Typography variant="body2">{device.timezone}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Digital Twin State */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('device.liveTwinState')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {!twin ? (
                <Alert severity="info" icon={false}>
                  {t('device.noLiveData')}
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .75 }}>
                      <Typography variant="body2" fontWeight={600}>{t('device.health')}</Typography>
                      <Typography variant="body2" fontWeight={800}>{twin.health_score}/100</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, twin.health_score))}
                      sx={{ height: 7, borderRadius: 5, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: twin.health_score >= 70 ? '#22c55e' : twin.health_score >= 40 ? '#f59e0b' : '#ef4444' } }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('device.lastSeen')}</Typography>
                    <Typography variant="body2">
                      {twin.last_seen_at ? format(new Date(twin.last_seen_at), 'PPp') : '—'}
                    </Typography>
                  </Box>
                  {twin.last_latitude != null && twin.last_longitude != null && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('device.latitude')}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{twin.last_latitude.toFixed(6)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('device.longitude')}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{twin.last_longitude.toFixed(6)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">{t('device.speed')}</Typography>
                        <Typography variant="body2">{twin.last_speed ?? 0} km/h</Typography>
                      </Box>
                    </>
                  )}
                  {twin.last_heading != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('device.heading')}</Typography>
                      <Typography variant="body2">{twin.last_heading.toFixed(0)}°</Typography>
                    </Box>
                  )}
                  {twin.last_altitude != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('device.altitude')}</Typography>
                      <Typography variant="body2">{twin.last_altitude.toFixed(0)} m</Typography>
                    </Box>
                  )}
                  {twin.updated_at && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{t('device.updated')}</Typography>
                      <Typography variant="body2">{format(new Date(twin.updated_at), 'PPp')}</Typography>
                    </Box>
                  )}
                  {twin.sensor_data && Object.keys(twin.sensor_data).length > 0 && (
                    <>
                      <Divider />
                      {Object.entries(twin.sensor_data).map(([k, v]) => (
                        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography variant="body2" color="text.secondary">{k.replace(/_/g, ' ')}</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere', textAlign: 'end' }}>
                            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                          </Typography>
                        </Box>
                      ))}
                    </>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Attached Sensors */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('device.attachedSensors')} ({sensors.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {sensors.length === 0 ? (
                <Alert severity="info" icon={false}>{t('device.noSensors')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {sensors.map((s) => (
                    <Box
                      key={s.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          key: {s.key}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={s.data_type} size="small" variant="outlined" />
                        {s.unit && <Chip label={s.unit} size="small" />}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Fuel Records */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('device.fuelRecords')} ({fuelRecords.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {fuelRecords.length === 0 ? (
                <Alert severity="info" icon={false}>{t('device.noFuelRecords')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {fuelRecords.slice(0, 10).map((f: FuelRecord) => (
                    <Box key={f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: f.flagged_theft ? '#fef2f2' : 'action.hover' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {f.level_l != null ? `${f.level_l} L` : '—'}
                          {f.delta_l != null && <span style={{ color: f.delta_l >= 0 ? '#10b981' : '#ef4444' }}> ({f.delta_l >= 0 ? '+' : ''}{f.delta_l} L)</span>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(f.recorded_at).toLocaleString()} · {f.source}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {f.cost != null && f.cost > 0 && <Chip label={`$${f.cost.toFixed(2)}`} size="small" variant="outlined" />}
                        {f.flagged_theft && <Chip label={t('device.theftSuspected')} size="small" color="error" />}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Trips */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('device.tripHistory')} ({trips.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {trips.length === 0 ? (
                <Alert severity="info" icon={false}>{t('device.noTrips')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {trips.slice(0, 20).map((trip: Trip) => (
                    <Box
                      key={trip.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {trip.distance_km.toFixed(1)} km &middot;{' '}
                          {trip.duration_sec >= 3600
                            ? `${Math.floor(trip.duration_sec / 3600)}h ${Math.round((trip.duration_sec % 3600) / 60)}m`
                            : `${Math.round(trip.duration_sec / 60)}m`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {trip.started_at ? format(new Date(trip.started_at), 'PPp') : '—'}
                          {trip.ended_at ? ` → ${format(new Date(trip.ended_at), 'p')}` : ' (active)'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={`Max ${trip.max_speed_kph} km/h`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Avg ${trip.avg_speed_kph} km/h`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={trip.status}
                          size="small"
                          color={trip.status === 'completed' ? 'success' : 'warning'}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Share Links */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  {t('share.activeLinks')} ({deviceShareLinks.length})
                </Typography>
                <Button size="small" startIcon={<Share />} onClick={() => setShareOpen(true)}>
                  {t('share.createLink')}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {deviceShareLinks.length === 0 ? (
                <Alert severity="info" icon={false}>{t('share.noLinks')}</Alert>
              ) : (
                <Stack spacing={1}>
                  {deviceShareLinks.map((link: any) => (
                    <Box key={link.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinkIcon sx={{ fontSize: 16, color: '#34A8E0' }} />
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {link.label || t('share.untitled')}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.3 }}>
                          {link.url}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('share.expiresAt')}: {format(new Date(link.expires_at), 'PPp')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('share.copyLink')}>
                          <IconButton size="small" onClick={() => handleCopyLink(link.url)}>
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('share.revoke')}>
                          <IconButton size="small" color="error" onClick={() => deleteShare.mutate(link.id)}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Share Link Dialog */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('share.createLink')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('share.label')} value={shareLabel} onChange={(e) => setShareLabel(e.target.value)}
              placeholder={device?.name || ''} fullWidth size="small" />
            <TextField label={t('share.expiresHours')} type="number" value={shareHours}
              onChange={(e) => setShareHours(Math.max(1, Math.min(720, Number(e.target.value))))}
              inputProps={{ min: 1, max: 720 }} fullWidth size="small"
              helperText={t('share.expiresHelp')} />
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">{t('share.destination')}</Typography>
            <Stack direction="row" spacing={1}>
              <TextField label={t('share.destLat')} value={destLat} onChange={(e) => setDestLat(e.target.value)}
                size="small" sx={{ flex: 1 }} />
              <TextField label={t('share.destLng')} value={destLng} onChange={(e) => setDestLng(e.target.value)}
                size="small" sx={{ flex: 1 }} />
            </Stack>
            <TextField label={t('share.destLabel')} value={destLabel} onChange={(e) => setDestLabel(e.target.value)}
              placeholder="e.g. Customer Location" fullWidth size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreateShare} disabled={createShare.isPending}
            sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)' }}>
            {createShare.isPending ? <CircularProgress size={20} /> : t('share.generate')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}
